import React, { Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Divider, Tooltip } from '@material-ui/core';
import classNames from 'classnames';
import { ToCurrencyString, ToCurrencyStringWithDecimals, ToPercentageString, ToPercentageStringWithDecimals } from 'services/formatService';
import SentimentVeryDissatisfiedIcon from '@material-ui/icons/SentimentVeryDissatisfied';
import { Constants } from 'utilities/constants';
import WaveIcon from '@material-ui/icons/Waves';

const PropertyUWProformaRent = ({ classes, valuationOutput, shouldCalculateProforma, selectedOpportunity, exceedsMaxBidPrice, isBelowB2LFloor }) => {
  const canDisplay = (number) => {
    if (number == null || isNaN(number) || number === 0) {
      return false;
    }
    return true;
  }

  const GetCurrencyString = (number, isNegative = false, fontBold = false, additionalClass = null) => {
    return (<Typography className={classNames(classes.valueSpacing, classes.tabContent, (isNegative ? classes.fontRed : null), (fontBold ? classes.fontBold : null), additionalClass)} variant='body2'>
      {canDisplay(number) ? (isNegative ? `(${ToCurrencyString(Math.abs(number))})` : `${ToCurrencyString(number)}`) : '-'}
    </Typography>)
  }

  return (
    (<div style={{ opacity: shouldCalculateProforma ? 0.4 : 1 }}>
      {(valuationOutput.isSuccess && !valuationOutput.rentAssumptionBestFitIcbmOrderId) ?
        // If calculation success and there is no matching ICBM order then we hide detail output contents and show no matching message instead 
        <div className={classes.noContentPaper}>
          <SentimentVeryDissatisfiedIcon color='disabled' className={classes.noMatchingErrorIcon} />
          <Typography variant='subtitle1' color='textSecondary'>No matching ICBM order for rent</Typography>
        </div>
        :
        // Detailed output items
        <Fragment>
          <Typography variant='subtitle2' align='left'>{'INCOME'}</Typography>
          <div className={classes.groupContainer}>
            {/* Gross Potential Rent */}
            <div className={classes.proformaSpacing}>
              <Tooltip title={'Gross Potential Rent'}>
                <Typography variant='body2' className={classes.tabContent}>{'GPR'}</Typography>
              </Tooltip>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.grossPotentialRent ? `${ToCurrencyString(valuationOutput.grossPotentialRent / 12)}/mo` : null}
              </Typography>
              {GetCurrencyString(valuationOutput.grossPotentialRent)}
            </div>
            {/* Vacancy Loss */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Vacancy Loss'}</Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.rentAssumptionBestFitIcbmOrder?.occupancy ? ToPercentageString(1 - valuationOutput.rentAssumptionBestFitIcbmOrder.occupancy) : null}
              </Typography>
              {GetCurrencyString(valuationOutput.occupancyLoss, true)}
            </div>
            {/* Credit Loss */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Credit Loss'}</Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.rentAssumptionBestFitIcbmOrder?.creditLoss ? ToPercentageString(valuationOutput.rentAssumptionBestFitIcbmOrder.creditLoss) : null}
              </Typography>
              {GetCurrencyString(valuationOutput.creditLoss, true)}
            </div>
            <Divider variant='middle' />
            <div className={classes.proformaSpacing}>
              <Tooltip title={'Effective Gross Income'}>
                <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>
                  {'EGI'}
                </Typography>
              </Tooltip>
              {GetCurrencyString(valuationOutput.effectiveGrossIncome, false, true)}
            </div>
          </div>

          <div className={classes.groupContainer} style={{ 'marginTop': '5px' }}>
            {/* Other Income */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Other Income'}</Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.rentAssumptionBestFitIcbmOrder?.otherIncome ? ToPercentageString(valuationOutput.rentAssumptionBestFitIcbmOrder.otherIncome) : null}
              </Typography>
              {GetCurrencyString(valuationOutput.otherIncome)}
            </div>
            {/* Concessions */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Concessions'}</Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.rentAssumptionBestFitIcbmOrder?.concessions ? ToPercentageString(valuationOutput.rentAssumptionBestFitIcbmOrder.concessions) : null}
              </Typography>
              {GetCurrencyString(valuationOutput.concessions, true)}
            </div>
            <Divider variant='middle' />
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>
                {'Operating Income'}
              </Typography>
              {GetCurrencyString(valuationOutput.operatingIncome, false, true)}
            </div>
          </div>

          <div className={classes.groupContainer} style={{ 'marginTop': '5px' }}>
            {/* Property Taxes */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Property Taxes'}</Typography>
              {GetCurrencyString(valuationOutput.taxes, true)}
            </div>
            {/* Insurance */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Insurance'}</Typography>
              <Typography className={classes.iconSubContent} variant='body2'>

                {
                  valuationOutput.isInSpecialFloodHazardArea &&
                  <Tooltip title="Estimate includes special flood hazard rate" placement="bottom">
                    <WaveIcon fontSize="inherit" />
                  </Tooltip>
                }

              </Typography>
              {GetCurrencyString(valuationOutput.insurance, true)}
            </div>
            {/* HOA */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'HOA'}</Typography>
              {GetCurrencyString(valuationOutput.hoa, true)}
            </div>
            {/* R&M */}
            <div className={classes.proformaSpacing}>
              <Tooltip title={'Maintenance'}>
                <Typography variant='body2' className={classes.tabContent}>{'R&M'}</Typography>
              </Tooltip>
              {GetCurrencyString(valuationOutput.maintenance, true)}
            </div>
            {/* Net Turnover */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Net Turnover'}</Typography>
              {GetCurrencyString(valuationOutput.turnover, true)}
            </div>
            {/* Leasing Fee */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Leasing Fee'}</Typography>
              {GetCurrencyString(valuationOutput.leasingCost, true)}
            </div>
            {/* PM Fee */}
            <div className={classes.proformaSpacing}>
              <Tooltip title={'managementRate'}>
                <Typography variant='body2' className={classes.tabContent}>{'PM Fee'}</Typography>
              </Tooltip>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.rentAssumptionBestFitIcbmOrder?.managementRate ? ToPercentageString(valuationOutput.rentAssumptionBestFitIcbmOrder.managementRate) : null}
              </Typography>
              {GetCurrencyString(valuationOutput.management, true)}
            </div>
            <Divider variant='middle' />
            {/* Total Expense */}
            <div className={classNames(classes.proformaSpacing)}>
              <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>
                {'Total Expense'}
              </Typography>
              {GetCurrencyString(valuationOutput.operatingExpense, true, true)}
            </div>
          </div>

          {/* NOI */}
          <div className={classes.proformaSpacing} style={{ 'marginTop': '5px' }}>
            <Tooltip title={'Net Operating Income'}>
              <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>NOI</Typography>
            </Tooltip>
            {GetCurrencyString(valuationOutput.netOperatingIncome, false, true)}
          </div>

          {
            selectedOpportunity.channelText === Constants.Channel.AUCTION ?
              <div className={classes.proformaSpacing}>
                {/* Minimum Cap Rate */}
                <Tooltip title='Minimum Cap Rate'>
                  <Typography variant='body2' className={classNames(classes.tabContent)}>Min Cap Rate</Typography>
                </Tooltip>
                <Typography variant='body2' className={classNames(classes.valueSpacing, classes.tabContent)}>
                  {canDisplay(valuationOutput.minCapRate) ? ToPercentageString(valuationOutput.minCapRate) : '-'}
                </Typography>
              </div>
              :
              <div>
                <div className={classes.proformaSpacing}>
                  {/* Price */}
                  <Tooltip title='Bid Price'>
                    <Typography variant='body2' className={classNames(classes.tabContent)}>Price</Typography>
                  </Tooltip>
                  <Typography className={classNames(classes.tabSubContent, isBelowB2LFloor && classes.warningTextColor)} variant='body2'>
                    {canDisplay(valuationOutput.bidPricePctOfAskingPrice) ? `${ToPercentageString(valuationOutput.bidPricePctOfAskingPrice)} of List` : null}
                  </Typography>
                  <Typography variant='body2' className={classNames(classes.valueSpacing, classes.tabContent)}>
                    {canDisplay(valuationOutput.bidPrice) ? ToCurrencyString(valuationOutput.bidPrice) : '-'}
                  </Typography>
                </div>
                <div className={classes.proformaSpacing}>
                  {/* IC Max Bid */}
                  <Tooltip title='IC Max Bid'>
                    <Typography variant='body2' className={classNames(classes.tabContent)}>IC Max Bid</Typography>
                  </Tooltip>
                  <Typography className={classes.tabSubContent} variant='body2'>
                    {(canDisplay(selectedOpportunity.askingPrice) && canDisplay(valuationOutput.rentAssumptionBid)) ? `${ToPercentageString(valuationOutput.rentAssumptionBid / selectedOpportunity.askingPrice)} of List` : null}
                  </Typography>
                  <Typography variant='body2' className={classNames(classes.valueSpacing, classes.tabContent)}>
                    {canDisplay(valuationOutput.rentAssumptionBid) ? ToCurrencyString(valuationOutput.rentAssumptionBid) : '-'}
                  </Typography>
                </div>
              </div>
          }

          <Typography variant='subtitle2' align='left'>{'COST BASIS'}</Typography>
          <div className={classes.groupContainer}>
            {/* Rental Cost Basis */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>
                {'Rental Cost Basis'}
              </Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.rentCostBasis && valuationOutput.afterRepairValue && valuationOutput.afterRepairValue !== 0 ? `${ToPercentageStringWithDecimals(valuationOutput.rentCostBasis / valuationOutput.afterRepairValue, 1)} ARV` : null}
              </Typography>
              {GetCurrencyString(valuationOutput.rentCostBasis, false, true)}
            </div>
            {/* Carry Cost */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Carry Cost'}</Typography>
              {GetCurrencyString(valuationOutput.rentCarryCost, true)}
            </div>
            {/* Repairs */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Repairs'}</Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.rentAssumptionRepairEstimatePerSqft ? `${ToCurrencyStringWithDecimals(valuationOutput.rentAssumptionRepairEstimatePerSqft)}/sqft` : null}
              </Typography>
              {GetCurrencyString(valuationOutput.rentAssumptionRepairEstimate, true)}
            </div>
            {/* Acquisition Fees */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Acquisition Fees'}</Typography>
              {GetCurrencyString(valuationOutput.rentAcquisitionFees, true)}
            </div>
            {/* Closing Costs */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Closing Costs'}</Typography>
              {GetCurrencyString(valuationOutput.rentClosingCosts, true)}
            </div>

            <Divider variant='middle' />

            <Fragment>
              <div className={classes.proformaSpacing}>
                <Tooltip title='Rent Cap Rate'>
                  <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>Rent Cap Rate</Typography>
                </Tooltip>
                <Typography variant='body2' className={classNames(classes.fontBold, classes.valueSpacing, classes.tabContent)}>
                  {canDisplay(valuationOutput.capRate) ? ToPercentageString(valuationOutput.capRate) : '-'}
                </Typography>
              </div>
            </Fragment>

            <Divider variant='middle' />
            <Typography variant='subtitle2' align='left'>{'OFFER'}</Typography>
            <div className={classes.groupContainer}>
              <div className={classNames(classes.proformaSpacing)}>
                <Typography variant='body2' className={classNames(classes.tabContent)}>
                  {'Recommended'}
                </Typography>
                <Typography className={classes.tabSubContent} variant='body2'>
                  {canDisplay(valuationOutput.rentRecommendedOfferAmount) ? `${ToPercentageString(valuationOutput.rentRecommendedOfferAmount / selectedOpportunity.askingPrice)} of List` : null}
                </Typography>
                {GetCurrencyString(valuationOutput.rentRecommendedOfferAmount)}
              </div>
              <div className={classNames(classes.proformaSpacing)}>
                <Typography variant='body2' className={classNames(classes.tabContent)}>
                  {'Maximum'}
                </Typography>
                <Typography className={classes.tabSubContent} variant='body2'>
                  {canDisplay(valuationOutput.rentMaxOfferAmount) ? `${ToPercentageString(valuationOutput.rentMaxOfferAmount / selectedOpportunity.askingPrice)} of List` : null}
                </Typography>
                {GetCurrencyString(valuationOutput.rentMaxOfferAmount, false, false, exceedsMaxBidPrice ? classes.warningTextColor : null)}
              </div>
            </div>
          </div>

          <br />
        </Fragment>
      }

    </div >)
  )
};

const styles = theme => ({
  noContentPaper: {
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  noMatchingErrorIcon: {
    fontSize: '2rem'
  },
  tabHeader: {
    fontSize: '14px',
  },
  tabContent: {
    fontSize: '12px',
  },
  tabSubContent: {
    flexGrow: '1',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: '10px',
    fontSize: '0.6rem',
    color: 'gray',
  },
  iconSubContent: {
    flexGrow: '1',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: '10px',
    fontSize: '0.8rem',
  },
  proformaSpacing: {
    display: 'flex',
    width: '100%',
    padding: '0 10px',
  },
  fontBold: {
    fontWeight: 'bold',
  },
  fontRed: {
    color: theme.palette.red.main,
  },
  valueSpacing: {
    marginLeft: 'auto',
  },
  groupContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white'
  },
  warningTextColor: {
    color: theme.palette.warning.text,
    fontWeight: 'bold'
  },
})
export default withStyles(styles)(PropertyUWProformaRent)










